import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService, type DashboardStats, type ConsultaDia, type ConsultaSemProntuario } from '../services/api/dashboardService';
import { formatarMoeda } from '../utils/formatters';

// Função para formatar data sem conversão de timezone
const formatarDataSemTimezone = (dataString: string): string => {
//   console.log('[Dashboard] formatarDataSemTimezone - Input:', {
//     dataString,
//     tipo: typeof dataString,
//     timestamp: new Date().toISOString()
//   });
  
  // Se a data já vem no formato correto do banco (YYYY-MM-DD), usar diretamente
  const dataLimpa = dataString.split('T')[0]; // Pegar apenas a parte da data
//   console.log('[Dashboard] formatarDataSemTimezone - Data limpa:', dataLimpa);
  
  const partes = dataLimpa.split('-');
//   console.log('[Dashboard] formatarDataSemTimezone - Partes:', partes);
  
  if (partes.length === 3) {
    const [ano, mes, dia] = partes;
    const resultado = `${dia}/${mes}/${ano}`;
//     console.log('[Dashboard] formatarDataSemTimezone - Resultado:', {
//       ano, mes, dia, resultado
//     });
    return resultado;
  }
  
//   console.warn('[Dashboard] formatarDataSemTimezone - Fallback usado para:', dataString);
  return dataString; // Fallback se não conseguir processar
};

export default function Dashboard() {
  const navigate = useNavigate();
  // Abas mobile
  const [mobileTab, setMobileTab] = useState<'stats' | 'dia' | 'prontuarios'>('stats');
  
  // Estados principais
  const [carregando, setCarregando] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [dataAtual, setDataAtual] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [semanaAtual, setSemanaAtual] = useState(() => {
    // Sempre começar com a segunda-feira da semana atual
    const hoje = new Date();
    const diaSemana = hoje.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
    const diasParaSegunda = diaSemana === 0 ? -6 : 1 - diaSemana; // Se domingo, voltar 6 dias
    const segundaFeira = new Date(hoje);
    segundaFeira.setDate(hoje.getDate() + diasParaSegunda);
//     console.log('[Dashboard] Semana inicial - Segunda-feira:', segundaFeira.toISOString().split('T')[0]);
    return segundaFeira.toISOString().split('T')[0];
  });
  const [consultasDoDia, setConsultasDoDia] = useState<ConsultaDia[]>([]);
  const [consultasSemProntuario, setConsultasSemProntuario] = useState<ConsultaSemProntuario[]>([]);

  // Função para carregar todos os dados do dashboard
  const carregarDadosDashboard = useCallback(async () => {
    try {
      setCarregando(true);
      
//       console.log('[Dashboard] Carregando dados para data:', dataAtual);
//       console.log('[Dashboard] Carregando dados para semana iniciando em:', semanaAtual);
      
      // Carregar estatísticas gerais (usando semana para horários)
      const statsData = await dashboardService.obterEstatisticas(semanaAtual);
//       console.log('[Dashboard] Estatísticas carregadas:', statsData);
//       console.log('[Dashboard] Horários livres da semana recebidos:', statsData.horariosLivresSemana);
      
      // Log detalhado dos horários para debug
      if (statsData.horariosLivresSemana && statsData.horariosLivresSemana.length > 0) {
//         console.log('[Dashboard] Análise detalhada dos horários livres:');
        statsData.horariosLivresSemana.forEach((dia, index) => {
//           console.log(`[Dashboard] Dia ${index + 1}:`, {
//             data: dia.data,
//             dia_semana: dia.dia_semana,
//             dia_semana_tipo: typeof dia.dia_semana,
//             total_disponivel: dia.total_disponivel,
//             total_ocupado: dia.total_ocupado,
//             horarios_count: dia.horarios.length,
//             horarios_sample: dia.horarios.slice(0, 3) // Mostrar apenas os 3 primeiros
//           });
        });
      } else {
//         console.warn('[Dashboard] PROBLEMA: Nenhum horário livre recebido ou array vazio');
      }
      
      setStats(statsData);
      
      // Carregar consultas do dia específico
//       console.log('[Dashboard] Carregando consultas para o dia:', dataAtual);
      const consultasDia = await dashboardService.obterConsultasDoDia(dataAtual);
//       console.log('[Dashboard] Consultas do dia carregadas:', consultasDia);
      setConsultasDoDia(consultasDia);
      
      // Carregar consultas sem prontuário (independente da data)
//       console.log('[Dashboard] Carregando consultas sem prontuário');
      const consultasSemPront = await dashboardService.obterConsultasSemProntuario(10);
//       console.log('[Dashboard] Consultas sem prontuário carregadas:', consultasSemPront);
      setConsultasSemProntuario(consultasSemPront);
      
//       console.log('[Dashboard] Dados carregados com sucesso:', { 
//         data: dataAtual,
//         semana: semanaAtual,
//         stats: statsData, 
//         consultasDia,
//         consultasSemProntuario: consultasSemPront.length
//       });
      
    } catch (error) {
      console.error('[Dashboard] Erro ao carregar dados:', error);
    } finally {
      setCarregando(false);
    }
  }, [dataAtual, semanaAtual]);

  // Carregar dados ao montar e quando mudar data
  useEffect(() => {
    carregarDadosDashboard();
  }, [carregarDadosDashboard]);

  // Funções de navegação de data
  const navegarDiaAnterior = () => {
//     console.log('[Dashboard] Navegando para dia anterior - Data atual:', dataAtual);
    const novaData = dashboardService.obterDataAnterior(dataAtual);
//     console.log('[Dashboard] Nova data calculada:', novaData);
    setDataAtual(novaData);
  };

  const navegarProximoDia = () => {
//     console.log('[Dashboard] Navegando para próximo dia - Data atual:', dataAtual);
    const novaData = dashboardService.obterDataPosterior(dataAtual);
//     console.log('[Dashboard] Nova data calculada:', novaData);
    setDataAtual(novaData);
  };

  const navegarParaHoje = () => {
//     console.log('[Dashboard] Navegando para hoje');
    const hoje = new Date().toISOString().split('T')[0];
//     console.log('[Dashboard] Data de hoje:', hoje);
    setDataAtual(hoje);
  };

  // Funções de navegação de semana
  const navegarSemanaAnterior = () => {
    const dataAtualObj = new Date(semanaAtual);
    dataAtualObj.setDate(dataAtualObj.getDate() - 7);
    const novaSegunda = dataAtualObj.toISOString().split('T')[0];
//     console.log('[Dashboard] Navegando para semana anterior - de:', semanaAtual, 'para:', novaSegunda);
    setSemanaAtual(novaSegunda);
  };

  const navegarProximaSemana = () => {
    const dataAtualObj = new Date(semanaAtual);
    dataAtualObj.setDate(dataAtualObj.getDate() + 7);
    const novaSegunda = dataAtualObj.toISOString().split('T')[0];
//     console.log('[Dashboard] Navegando para próxima semana - de:', semanaAtual, 'para:', novaSegunda);
    setSemanaAtual(novaSegunda);
  };

  const navegarParaSemanaAtual = () => {
//     console.log('[Dashboard] Navegando para semana atual');
    const hoje = new Date();
    const diaSemana = hoje.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
    const diasParaSegunda = diaSemana === 0 ? -6 : 1 - diaSemana;
    const segundaFeira = new Date(hoje);
    segundaFeira.setDate(hoje.getDate() + diasParaSegunda);
    const segundaFormatada = segundaFeira.toISOString().split('T')[0];
//     console.log('[Dashboard] Segunda-feira da semana atual:', segundaFormatada);
    setSemanaAtual(segundaFormatada);
  };

  // Função para formatar o período da semana
  const formatarPeriodoSemana = (segundaFeira: string) => {
    const segunda = new Date(segundaFeira);
    const domingo = new Date(segunda);
    domingo.setDate(segunda.getDate() + 6);
    
    const formatarData = (data: Date) => {
      return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    };
    
    return `${formatarData(segunda)} - ${formatarData(domingo)}`;
  };

  if (carregando) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
        <p className="mt-3">Carregando dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Tabs mobile */}
      <div className="d-md-none mb-3">
        <ul className="nav nav-tabs mobile-dashboard-tabs">
          <li className="nav-item flex-fill">
            <button
              className={`nav-link w-100 ${mobileTab === 'stats' ? 'active' : ''}`}
              onClick={() => setMobileTab('stats')}
            >
              <i className="fas fa-chart-line mr-1"></i>
              Estatísticas
            </button>
          </li>
          <li className="nav-item flex-fill">
            <button
              className={`nav-link w-100 ${mobileTab === 'dia' ? 'active' : ''}`}
              onClick={() => setMobileTab('dia')}
            >
              <i className="fas fa-calendar-day mr-1"></i>
              Hoje
            </button>
          </li>
          <li className="nav-item flex-fill">
            <button
              className={`nav-link w-100 ${mobileTab === 'prontuarios' ? 'active' : ''}`}
              onClick={() => setMobileTab('prontuarios')}
            >
              <i className="fas fa-file-medical mr-1"></i>
              Prontuários
            </button>
          </li>
        </ul>
      </div>
  {/* Cards de estatísticas principais */}
  <div className={`row ${mobileTab !== 'stats' ? 'd-none d-md-flex' : ''}`}>
        <div className="col-lg-3 col-6">
          <div className="small-box bg-info">
            <div className="inner">
              <h3>{stats?.consultasHoje || 0}</h3>
              <p>Consultas Hoje</p>
            </div>
            <div className="icon">
              <i className="fas fa-calendar-day"></i>
            </div>
            <div className="small-box-footer">
              {dashboardService.formatarDataExibicao(dataAtual)}
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-6">
          <div className="small-box bg-warning">
            <div className="inner">
              <h3>{stats?.consultasRealizadasSemProntuario || 0}</h3>
              <p>Prontuários Pendentes</p>
            </div>
            <div className="icon">
              <i className="fas fa-file-medical"></i>
            </div>
            <div className="small-box-footer">
              Consultas sem prontuário
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-6">
          <div className="small-box bg-danger">
            <div className="inner">
              <h3>{stats?.pagamentosPendentes || 0}</h3>
              <p>Pagamentos Pendentes</p>
            </div>
            <div className="icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="small-box-footer">
              Consultas não pagas
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-6">
          <div className="small-box bg-success">
            <div className="inner">
              <h3>{formatarMoeda(stats?.previsaoFaturamentoMes || 0)}</h3>
              <p>Previsão do Mês</p>
            </div>
            <div className="icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="small-box-footer">
              Faturamento esperado
            </div>
          </div>
        </div>
      </div>

  {/* Seção principal: Consultas do dia */}
  <div className={`row ${mobileTab !== 'dia' ? 'd-none d-md-flex' : ''}`}>
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-calendar-day mr-2"></i>
                Consultas do Dia
              </h3>
              <div className="card-tools">
                <div className="btn-group">
                  <button 
                    className="btn btn-tool" 
                    onClick={navegarDiaAnterior}
                    title="Dia anterior"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button 
                    className="btn btn-tool" 
                    onClick={navegarParaHoje}
                    title="Hoje"
                  >
                    <i className="fas fa-home"></i>
                  </button>
                  <button 
                    className="btn btn-tool" 
                    onClick={navegarProximoDia}
                    title="Próximo dia"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              <h5 className="mb-3">
                {dashboardService.formatarDataExibicao(dataAtual)}
              </h5>
              
              {consultasDoDia.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-calendar-times fa-3x mb-3"></i>
                  <p>Nenhuma consulta agendada para este dia</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => navigate('/consultas/nova')}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Agendar Consulta
                  </button>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {consultasDoDia.map((consulta) => (
                    <div key={consulta.id} className="list-group-item list-group-item-action p-2">
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <div className="d-flex align-items-center flex-grow-1">
                          {/* Horário */}
                          <div className="text-center mr-3" style={{ minWidth: '50px' }}>
                            <span className={`badge badge-${consulta.realizada ? 'success' : consulta.statusConsulta === 'cancelada' ? 'danger' : 'info'} p-2`}>
                              {consulta.hora_inicio || consulta.hora}
                            </span>
                          </div>
                          
                          {/* Info do paciente */}
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <strong className="mb-0 mr-2">{consulta.nome_paciente}</strong>
                              <span className={`badge badge-outline-${consulta.realizada ? 'success' : consulta.statusConsulta === 'cancelada' ? 'danger' : 'primary'} mr-2`}>
                                {consulta.realizada ? 'Realizada' : consulta.statusConsulta === 'cancelada' ? 'Cancelada' : 'Agendada'}
                              </span>
                              {consulta.realizada && !consulta.pagamentoRecebido && (
                                <span className="badge badge-warning mr-2">
                                  Pag. Pendente
                                </span>
                              )}
                              <span className="text-success font-weight-bold">
                                {formatarMoeda(consulta.valor)}
                              </span>
                            </div>
                            <small className="text-muted d-block">
                              <i className="fas fa-phone mr-1"></i>
                              {consulta.telefone || 'Telefone não informado'}
                            </small>
                          </div>
                        </div>
                        
                        {/* Ações */}
                        <div className="btn-group btn-group-sm ml-2">
                          <button 
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate(`/consultas/${consulta.id}/editar`)}
                            title="Editar consulta"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          {consulta.realizada && (
                            <button 
                              className="btn btn-outline-success btn-sm"
                              onClick={() => navigate(`/consultas/${consulta.id}/prontuario`)}
                              title="Ver prontuário"
                            >
                              <i className="fas fa-file-medical"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

  {/* Seção: Consultas sem Prontuário */}
  <div className={`col-md-6 ${mobileTab !== 'prontuarios' ? 'd-none d-md-block' : ''}`}>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-file-medical mr-2"></i>
                Prontuários Pendentes
              </h3>
              <div className="card-tools">
                <span className="badge badge-warning">
                  {consultasSemProntuario.length}
                </span>
              </div>
            </div>
            <div className="card-body">
              {consultasSemProntuario.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-check-circle fa-3x mb-3 text-success"></i>
                  <p>Todos os prontuários estão em dia!</p>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-sm table-striped">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th>Paciente</th>
                        <th>Data</th>
                        <th>Dias</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consultasSemProntuario.map((consulta) => (
                        <tr key={consulta.id} className={consulta.dias_desde_consulta > 7 ? 'table-danger' : consulta.dias_desde_consulta > 3 ? 'table-warning' : ''}>
                          <td>
                            <strong>{consulta.nome_paciente}</strong>
                            <br />
                            <small className="text-muted">
                              {consulta.hora_inicio} - {formatarMoeda(consulta.valor)}
                            </small>
                          </td>
                          <td>
                            {formatarDataSemTimezone(consulta.data_consulta)}
                          </td>
                          <td>
                            <span className={`badge ${
                              consulta.dias_desde_consulta > 7 ? 'badge-danger' : 
                              consulta.dias_desde_consulta > 3 ? 'badge-warning' : 
                              'badge-info'
                            }`}>
                              {consulta.dias_desde_consulta}d
                            </span>
                          </td>
                          <td>
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => navigate(`/consultas/${consulta.id}/prontuario`)}
                              title="Criar prontuário"
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

  {/* Seção: Horários Livres da Semana */}
  <div className={`row ${mobileTab !== 'stats' ? 'd-none d-md-flex' : ''}`}>
        <div className="col-md-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h3 className="card-title mb-0">
                  <i className="fas fa-clock mr-2"></i>
                  Horários Disponíveis na Semana
                </h3>
                <small className="text-muted d-block mt-1">
                  {formatarPeriodoSemana(semanaAtual)}
                </small>
              </div>
              <div className="d-flex align-items-center">
                <div className="btn-group mr-3">
                  <button 
                    className="btn btn-sm btn-outline-primary" 
                    onClick={navegarSemanaAnterior}
                    title="Semana anterior"
                  >
                    <i className="fas fa-chevron-left mr-1"></i>
                    Anterior
                  </button>
                  <button 
                    className="btn btn-sm btn-primary" 
                    onClick={navegarParaSemanaAtual}
                    title="Semana atual"
                  >
                    <i className="fas fa-home mr-1"></i>
                    Atual
                  </button>
                  <button 
                    className="btn btn-sm btn-outline-primary" 
                    onClick={navegarProximaSemana}
                    title="Próxima semana"
                  >
                    Próxima
                    <i className="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
                <span className="badge badge-info badge-lg">
                  {stats?.horariosLivresSemana?.reduce((total, dia) => total + dia.total_disponivel, 0) || 0} horários livres
                </span>
              </div>
            </div>
            <div className="card-body p-3">
              {!stats?.horariosLivresSemana || stats.horariosLivresSemana.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-calendar-times fa-4x mb-3"></i>
                  <h5>Nenhum horário disponível configurado</h5>
                  <p className="mb-4">Configure seus horários de trabalho nas configurações para visualizar a disponibilidade da semana</p>
                  <button 
                    className="btn btn-primary btn-lg"
                    onClick={() => navigate('/configuracoes')}
                  >
                    <i className="fas fa-cog mr-2"></i>
                    Configurar Horários de Trabalho
                  </button>
                </div>
              ) : (
                <div className="row">
                  {/* Mapear para o nome completo para exibição */}
                  {(() => {
                    const nomesDiasCompletos = {
                      'segunda': 'Segunda',
                      'terca': 'Terça', 
                      'quarta': 'Quarta',
                      'quinta': 'Quinta',
                      'sexta': 'Sexta',
                      'sabado': 'Sábado',
                      'domingo': 'Domingo'
                    };
                    
                    // Ordem dos dias da semana
                    const ordemDias = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
                    
                    // Filtrar apenas os dias que têm horários configurados e ordenar
                    const diasComHorarios = stats.horariosLivresSemana
                      .filter(dia => dia.horarios && dia.horarios.length > 0)
                      .sort((a, b) => {
                        const indexA = ordemDias.indexOf(a.dia_semana.toLowerCase());
                        const indexB = ordemDias.indexOf(b.dia_semana.toLowerCase());
                        return indexA - indexB;
                      });
                    
//                     console.log('[Dashboard] Dias com horários encontrados:', diasComHorarios.map(d => d.dia_semana));
                    
                    return diasComHorarios.map((dia) => {
                      const nomeDiaBackend = dia.dia_semana.toLowerCase();
                      
                      return (
                        <div key={dia.data} className="col-md col-sm-6 mb-3">
                          <div className="card card-outline card-info h-100">
                            <div className="card-header bg-light text-center p-2">
                              <h6 className="card-title mb-1 font-weight-bold">
                                {nomesDiasCompletos[nomeDiaBackend as keyof typeof nomesDiasCompletos]}
                              </h6>
                              <small className="text-muted d-block">
                                {formatarDataSemTimezone(dia.data)}
                              </small>
                              <div className="mt-2">
                                <span className="badge badge-success mr-1" title="Horários livres">
                                  <i className="fas fa-check mr-0"></i>{' '}{dia.total_disponivel}
                                </span>
                                <span className="badge badge-danger" title="Horários ocupados">
                                  <i className="fas fa-times mr-0"></i>{' '}{dia.total_ocupado}
                                </span>
                              </div>
                            </div>
                            <div className="card-body p-2">
                              <div className="list-group list-group-flush">
                                {dia.horarios.map((slot) => (
                                  <div
                                    key={slot.hora}
                                    className={`list-group-item p-2 border-0 ${slot.ocupado ? 'list-group-item-danger' : 'list-group-item-success'}`}
                                    title={slot.ocupado ? `Ocupado: ${slot.paciente || 'Bloqueado'}` : 'Horário disponível'}
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="font-weight-bold" style={{ fontSize: '0.9em' }}>
                                        {slot.hora}
                                      </span>
                                      <i className={`fas ${slot.ocupado ? 'fa-times text-danger' : 'fa-check text-success'}`}></i>
                                    </div>
                                    {slot.ocupado && slot.paciente && (
                                      <small className="text-muted d-block mt-1" style={{ fontSize: '0.8em' }}>
                                        {slot.paciente.length > 20 ? slot.paciente.substring(0, 20) + '...' : slot.paciente}
                                      </small>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Seção inferior: Ações rápidas */}
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-rocket mr-2"></i>
                Ações Rápidas
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                  <button 
                    className="btn btn-primary btn-block"
                    onClick={() => navigate('/pacientes/novo')}
                  >
                    <i className="fas fa-user-plus mb-2"></i><br />
                    Novo Paciente
                  </button>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                  <button 
                    className="btn btn-success btn-block"
                    onClick={() => navigate('/consultas/nova')}
                  >
                    <i className="fas fa-calendar-plus mb-2"></i><br />
                    Agendar Consulta
                  </button>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                  <button 
                    className="btn btn-info btn-block"
                    onClick={() => navigate('/agenda')}
                  >
                    <i className="fas fa-calendar-alt mb-2"></i><br />
                    Ver Agenda
                  </button>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                  <button 
                    className="btn btn-secondary btn-block"
                    onClick={() => navigate('/todo-list')}
                  >
                    <i className="fas fa-tasks mb-2"></i><br />
                    Lista de Tarefas
                  </button>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                  <button 
                    className="btn btn-warning btn-block"
                    onClick={() => navigate('/relatorios')}
                  >
                    <i className="fas fa-chart-bar mb-2"></i><br />
                    Relatórios
                  </button>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                  <button 
                    className="btn btn-dark btn-block"
                    onClick={() => navigate('/configuracoes')}
                  >
                    <i className="fas fa-cog mb-2"></i><br />
                    Configurações
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
